import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getBrokerProfile, updateBrokerProfile, BrokerProfile, AgentBoxNumber,
} from '../services/clearanceService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { useFunctionName } from '../hooks/useFunctionName';
import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';

const DUTY_METHODS = [
  { code: '1', label: '1 — 現金繳納' },
  { code: '2', label: '2 — 轉帳繳納' },
  { code: '5', label: '5 — 記帳繳納' },
  { code: '9', label: '9 — 保稅記帳' },
];

const emptyBox = (): AgentBoxNumber => ({ box_id: '', sub_box_id: '0', label: '', is_default: false });

const ClearanceBrokerProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError, showError } = useMessage();
  const pageTitle = useFunctionName('ee_clearance_broker_profile');
  const hasInit = useRef(false);

  const [, setProfile] = useState<BrokerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<BrokerProfile>>({});
  const [boxes, setBoxes] = useState<AgentBoxNumber[]>([]);
  const [newBox, setNewBox] = useState<AgentBoxNumber>(emptyBox());
  const [addingBox, setAddingBox] = useState(false);

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      const init = async () => {
        const valid = await validateSession('ClearanceBrokerProfilePage');
        if (!valid) return;
        await load();
      };
      init();
    }
  }, [permLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getBrokerProfile();
      setProfile(data);
      setForm(data);
      setBoxes(data.agent_box_numbers ?? []);
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, agent_box_numbers: boxes };
      const saved = await updateBrokerProfile(payload);
      setProfile(saved);
      setForm(saved);
      setBoxes(saved.agent_box_numbers ?? []);
      showSuccess('SYS020002', { name: pageTitle });
    } catch (err: any) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof BrokerProfile, val: string) => setForm(f => ({ ...f, [key]: val }));

  // ── box number helpers ───────────────────────────────────────────────────────
  const setDefault = (idx: number) => {
    setBoxes(prev => prev.map((b, i) => ({ ...b, is_default: i === idx })));
  };

  const removeBox = (idx: number) => {
    setBoxes(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length > 0 && !next.some(b => b.is_default)) {
        next[0] = { ...next[0], is_default: true };
      }
      return next;
    });
  };

  const confirmAddBox = () => {
    if (!newBox.box_id.trim()) {
      showError('ERR020006', { field: '箱號', detail: '箱號不可為空' });
      return;
    }
    setBoxes(prev => {
      const isFirst = prev.length === 0;
      const entry: AgentBoxNumber = {
        box_id: newBox.box_id.trim().padEnd(3).slice(0, 3),
        sub_box_id: (newBox.sub_box_id || '0').slice(0, 1),
        label: newBox.label?.trim() || undefined,
        is_default: isFirst,
      };
      return isFirst ? [entry] : [...prev, entry];
    });
    setNewBox(emptyBox());
    setAddingBox(false);
  };

  if (permLoading || loading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;

  const canEdit = hasPermission('ee_clearance_broker_profile', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="ee_clearance_broker_profile" />
      </div>

      <form onSubmit={handleSave}>
        <div style={{ display: 'grid', gap: '24px', maxWidth: 900 }}>

          {/* 箱號管理 */}
          <section style={{ background: '#f0f4ff', border: '1px solid #c5cae9', borderRadius: 8, padding: '20px 24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#1565c0', borderBottom: '1px solid #c5cae9', paddingBottom: 8 }}>
              📮 {t('clearance.brokerProfile.sectionBoxNumbers', '報關箱號管理（海關核發）')}
            </h3>

            {boxes.length === 0 ? (
              <p style={{ color: '#6c757d', fontSize: 13, margin: '0 0 12px' }}>
                尚未設定箱號，請新增至少一個海關核發箱號。
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                <thead>
                  <tr style={{ background: '#e8eaf6', textAlign: 'left' }}>
                    <th style={thStyle}>箱號</th>
                    <th style={thStyle}>附碼</th>
                    <th style={thStyle}>自訂名稱</th>
                    <th style={thStyle}>預設</th>
                    {canEdit && <th style={thStyle}>操作</th>}
                  </tr>
                </thead>
                <tbody>
                  {boxes.map((b, idx) => (
                    <tr key={idx} style={{ background: b.is_default ? '#e3f2fd' : '#fff', borderBottom: '1px solid #e0e0e0' }}>
                      <td style={tdStyle}><code style={{ fontWeight: 600 }}>{b.box_id}</code></td>
                      <td style={tdStyle}>{b.sub_box_id}</td>
                      <td style={tdStyle}>{b.label || <span style={{ color: '#aaa' }}>—</span>}</td>
                      <td style={tdStyle}>
                        {b.is_default
                          ? <span style={{ color: '#1565c0', fontWeight: 600 }}>★ 預設</span>
                          : canEdit
                            ? <button type="button" onClick={() => setDefault(idx)} style={linkBtnStyle}>設為預設</button>
                            : <span style={{ color: '#aaa' }}>—</span>
                        }
                      </td>
                      {canEdit && (
                        <td style={tdStyle}>
                          <button type="button" onClick={() => removeBox(idx)}
                            style={{ ...linkBtnStyle, color: '#e53e3e' }}>
                            移除
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {canEdit && !addingBox && (
              <button type="button" onClick={() => setAddingBox(true)}
                style={{ fontSize: 13, padding: '5px 14px', border: '1px solid #3949ab', borderRadius: 4, background: '#fff', color: '#3949ab', cursor: 'pointer' }}>
                + 新增箱號
              </button>
            )}

            {canEdit && addingBox && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 8, flexWrap: 'wrap', background: '#fff', padding: 12, borderRadius: 6, border: '1px solid #c5cae9' }}>
                <label style={{ ...labelStyle, width: 100 }}>
                  <span>箱號 <span style={{ color: '#e53e3e' }}>*</span></span>
                  <input value={newBox.box_id} onChange={e => setNewBox(p => ({ ...p, box_id: e.target.value }))}
                    maxLength={3} placeholder="XXX" style={{ ...inputStyle, width: '100%' }} autoFocus />
                  <small style={{ color: '#6c757d' }}>海關核發 3碼</small>
                </label>
                <label style={{ ...labelStyle, width: 80 }}>
                  <span>附碼</span>
                  <input value={newBox.sub_box_id} onChange={e => setNewBox(p => ({ ...p, sub_box_id: e.target.value }))}
                    maxLength={1} placeholder="0" style={{ ...inputStyle, width: '100%' }} />
                  <small style={{ color: '#6c757d' }}>1碼</small>
                </label>
                <label style={{ ...labelStyle, flex: 1, minWidth: 140 }}>
                  <span>自訂名稱</span>
                  <input value={newBox.label || ''} onChange={e => setNewBox(p => ({ ...p, label: e.target.value }))}
                    maxLength={30} placeholder="如：進口主箱" style={{ ...inputStyle, width: '100%' }} />
                </label>
                <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
                  <button type="button" onClick={confirmAddBox} className="btn-primary" style={{ fontSize: 13, padding: '6px 14px' }}>
                    確認新增
                  </button>
                  <button type="button" onClick={() => { setAddingBox(false); setNewBox(emptyBox()); }}
                    style={{ fontSize: 13, padding: '6px 14px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                    取消
                  </button>
                </div>
              </div>
            )}

            <p style={{ fontSize: 12, color: '#78909c', marginTop: 12, marginBottom: 0 }}>
              預設箱號會自動帶入報單；報關員可在報單編輯頁改為其他箱號。
            </p>
          </section>

          {/* 預設設定 */}
          <section style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '20px 24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#1565c0', borderBottom: '1px solid #dee2e6', paddingBottom: 8 }}>
              ⚙️ {t('clearance.brokerProfile.sectionDefaults', '申報預設值')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.defaultDutyMethod', '預設納稅辦法')}
                <select value={form.default_duty_method_code || '1'} onChange={e => set('default_duty_method_code', e.target.value)}
                  style={inputStyle} disabled={!canEdit}>
                  {DUTY_METHODS.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
                </select>
              </label>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.defaultStorageCode', '預設存倉地點')}
                <input value={form.default_storage_code || ''} onChange={e => set('default_storage_code', e.target.value)}
                  style={inputStyle} disabled={!canEdit} placeholder="如 TWKHH" />
              </label>
            </div>
          </section>

          {/* 報關行基本資料 */}
          <section style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '20px 24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#1565c0', borderBottom: '1px solid #dee2e6', paddingBottom: 8 }}>
              🏢 {t('clearance.brokerProfile.sectionInfo', '報關行資料')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.brokerNameZh', '公司中文名稱')}
                <input value={form.broker_name_zh || ''} onChange={e => set('broker_name_zh', e.target.value)}
                  style={inputStyle} disabled={!canEdit} />
              </label>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.brokerNameEn', '公司英文名稱')}
                <input value={form.broker_name_en || ''} onChange={e => set('broker_name_en', e.target.value)}
                  style={inputStyle} disabled={!canEdit} />
              </label>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.brokerTaxId', '統一編號')}
                <input value={form.broker_tax_id || ''} onChange={e => set('broker_tax_id', e.target.value)}
                  maxLength={10} style={inputStyle} disabled={!canEdit} />
              </label>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.brokerPhone', '聯絡電話')}
                <input value={form.broker_phone || ''} onChange={e => set('broker_phone', e.target.value)}
                  style={inputStyle} disabled={!canEdit} />
              </label>
              <label style={labelStyle}>
                {t('clearance.brokerProfile.brokerEmail', '電子郵件')}
                <input type="email" value={form.broker_email || ''} onChange={e => set('broker_email', e.target.value)}
                  style={inputStyle} disabled={!canEdit} />
              </label>
            </div>
          </section>

          {canEdit && (
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('common.saving', '儲存中…') : t('common.save')}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, fontWeight: 500, color: '#212529',
};
const inputStyle: React.CSSProperties = {
  padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14,
  marginTop: 2, background: '#fff',
};
const thStyle: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12, fontWeight: 600, color: '#3949ab',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px', fontSize: 13, verticalAlign: 'middle',
};
const linkBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline',
};

export default ClearanceBrokerProfilePage;
